import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid2 as Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Assignment,
  InsertDriveFile,
  Visibility,
  Edit,
  Send,
  Person,
  ArrowBack,
  Download,
  Comment,
  Build,
  Close,
  ArrowBackIos,
  ArrowForwardIos,
  PhotoCamera,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { SubmissionStatusChip } from "../components/StatusChips";
import { DueBadge } from "../components/DueBadges";
import PpMobilePhotoCaptureComponent from "../Photos/PpMobilePhotoCaptureComponent";
import PpPhotoAnnotationTools from "../Photos/PpPhotoAnnotationTools";

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";

const PpSubmissionDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(true);

  // File viewer states
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  // Photo capture states
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);

  // Mock submission data
  const mockSubmission = {
    id: id,
    permitNumber: "PKG2024001",
    address: "123 Oak Street, Jacksonville, FL 32207",
    contractor: {
      id: "C001",
      name: "ABC Construction",
      license: "CBC012345",
      email: "contact@abcconstruction.com",
      phone: "904-555-0123",
    },
    packageTier: "Residential Standard",
    city: "Duval County",
    type: "residential",
    status: "in_review",
    dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    dueBucket: "75%",
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    assignedReviewer: "John Smith, PE",
    notes: "Standard residential building permit review",
    submissionNotes:
      "Customer has requested expedited review due to construction timeline.",
    files: [
      {
        id: "PRF001-01-00",
        name: "Architectural Plans.pdf",
        type: "pdf",
        size: "2.5MB",
        uploadedAt: new Date(
          Date.now() - 2 * 24 * 60 * 60 * 1000
        ).toISOString(),
        documentType: "Architectural",
      },
      {
        id: "PRF001-02-00",
        name: "Structural Drawings.pdf",
        type: "pdf",
        size: "1.8MB",
        uploadedAt: new Date(
          Date.now() - 2 * 24 * 60 * 60 * 1000
        ).toISOString(),
        documentType: "Structural",
      },
      {
        id: "PRF001-03-00",
        name: "Energy Forms.pdf",
        type: "pdf",
        size: "1.2MB",
        uploadedAt: new Date(
          Date.now() - 2 * 24 * 60 * 60 * 1000
        ).toISOString(),
        documentType: "Energy",
      },
    ],
    workbench: {
      totalPages: 15,
      reviewedPages: 8,
      compliantPages: 6,
      needsCorrectionPages: 2,
      naPages: 0,
    },
    timeline: [
      {
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        action: "submitted",
        user: "ABC Construction",
        notes: "Initial submission uploaded",
      },
      {
        date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        action: "assigned",
        user: "System",
        notes: "Assigned to John Smith, PE for review",
      },
      {
        date: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        action: "review_started",
        user: "John Smith, PE",
        notes: "Review process initiated",
      },
    ],
    relatedInspections: [
      { id: "I001", name: "Footing/Foundation", status: "not_scheduled" },
      { id: "I002", name: "Framing", status: "not_scheduled" },
      { id: "I003", name: "Final", status: "not_scheduled" },
    ],
  };

  useEffect(() => {
    // In real app, load submission by ID
    setSubmission(mockSubmission);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    const note = {
      date: new Date().toISOString(),
      action: "note_added",
      user: "John Smith, PE", // Current user
      notes: newNote,
    };

    setSubmission((prev) => ({
      ...prev,
      timeline: [...prev.timeline, note],
    }));

    setNewNote("");
    setNoteDialogOpen(false);
  };

  const getTimelineDotColor = (action) => {
    const colorMap = {
      submitted: "primary",
      assigned: "info",
      review_started: "warning",
      approved: "success",
      returned: "error",
      note_added: "secondary",
    };
    return colorMap[action] || "grey";
  };

  const getFileIcon = (type) => {
    return <InsertDriveFile color={type === "pdf" ? "error" : "primary"} />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // File viewer handlers
  const handleViewFile = (file, allFiles) => {
    setSelectedFiles(allFiles);
    setCurrentFileIndex(allFiles.findIndex((f) => f.id === file.id));
    setSelectedFile(file);
    setFileViewerOpen(true);
  };

  const handleNextFile = () => {
    if (currentFileIndex < selectedFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
      setSelectedFile(selectedFiles[currentFileIndex + 1]);
    }
  };

  const handlePrevFile = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
      setSelectedFile(selectedFiles[currentFileIndex - 1]);
    }
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedFile(null);
    setSelectedFiles([]);
    setCurrentFileIndex(0);
  };

  const handleDownloadSingleFile = (file) => {
    const link = document.createElement("a");
    link.href = `/api/files/${file.id}/download`;
    link.download = file.name;
    link.click();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading submission details...</Typography>
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Submission not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/submissions")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="primary" variant="h4">
            Submission Details - {submission.id}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {submission.address}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <SubmissionStatusChip status={submission.status} />
          <DueBadge
            dueBucket={submission.dueBucket}
            dueDate={submission.dueDate}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ sm: 12, md: 6 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Permit Number"
                    secondary={submission.permitNumber || "Not assigned"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Package Tier"
                    secondary={submission.packageTier}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Type"
                    secondary={
                      submission.type.charAt(0).toUpperCase() +
                      submission.type.slice(1)
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="City/County"
                    secondary={submission.city}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Created"
                    secondary={formatDate(submission.createdAt)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Due Date"
                    secondary={formatDate(submission.dueDate)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Assigned Reviewer"
                    secondary={submission.assignedReviewer || "Not assigned"}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Contractor Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contractor Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText
                    primary={submission.contractor.name}
                    secondary={`License: ${submission.contractor.license}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Email"
                    secondary={submission.contractor.email}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Phone"
                    secondary={submission.contractor.phone}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Files and Progress */}
        <Grid size={{ sm: 12, md: 6 }}>
          {/* Review Progress */}
          {submission.workbench && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Review Progress</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/pp/workbench/${submission.id}`)}
                  >
                    Open Workbench
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  <Grid xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Pages:
                    </Typography>
                    <Typography variant="h6">
                      {submission.workbench.totalPages}
                    </Typography>
                  </Grid>
                  <Grid xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Reviewed:
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {submission.workbench.reviewedPages}
                    </Typography>
                  </Grid>
                  <Grid xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Compliant:
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {submission.workbench.compliantPages}
                    </Typography>
                  </Grid>
                  <Grid xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      Corrections:
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {submission.workbench.needsCorrectionPages}
                    </Typography>
                  </Grid>
                  <Grid xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      N/A:
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {submission.workbench.naPages}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Uploaded Files */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Uploaded Files ({submission.files.length})
              </Typography>
              <List>
                {submission.files.map((file) => (
                  <ListItem
                    key={file.id}
                    divider
                    secondaryAction={
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View File">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleViewFile(file, submission.files)
                            }
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadSingleFile(file)}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <ListItemIcon>{getFileIcon(file.type)}</ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {file.size} • {file.documentType} • ID: {file.id}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            Uploaded: {formatDate(file.uploadedAt)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Timeline and Related Items */}
        <Grid size={{ sm: 12 }}>
          <Grid container spacing={3}>
            {/* Activity Timeline */}
            <Grid size={{ sm: 12, md: 8 }}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">Activity Timeline</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Comment />}
                      onClick={() => setNoteDialogOpen(true)}
                    >
                      Add Note
                    </Button>
                  </Box>

                  <Timeline>
                    {submission.timeline.map((event, index) => (
                      <TimelineItem key={index}>
                        <TimelineSeparator>
                          <TimelineDot
                            color={getTimelineDotColor(event.action)}
                          />
                          {index < submission.timeline.length - 1 && (
                            <TimelineConnector />
                          )}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {event.action.replace("_", " ")}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {event.user} • {formatDate(event.date)}
                          </Typography>
                          {event.notes && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {event.notes}
                            </Typography>
                          )}
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </CardContent>
              </Card>
            </Grid>

            {/* Related Inspections */}
            <Grid size={{ sm: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Related Inspections
                  </Typography>

                  {submission.relatedInspections.length === 0 ? (
                    <Alert severity="info">
                      No inspections scheduled yet. Inspections will be
                      available after plan approval.
                    </Alert>
                  ) : (
                    <List dense>
                      {submission.relatedInspections.map((inspection) => (
                        <ListItem key={inspection.id}>
                          <ListItemIcon>
                            <Build fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={inspection.name}
                            secondary={
                              <Chip
                                label={inspection.status.replace("_", " ")}
                                color={
                                  inspection.status === "passed"
                                    ? "success"
                                    : "default"
                                }
                                size="small"
                                variant="outlined"
                              />
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {submission.workbench && (
                      <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() =>
                          navigate(`/pp/workbench/${submission.id}`)
                        }
                      >
                        Open Review Workbench
                      </Button>
                    )}

                    <Button
                      variant="outlined"
                      startIcon={<Send />}
                      onClick={() =>
                        navigate(`/pp/messaging/submission_${submission.id}`)
                      }
                    >
                      Message Contractor
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<Assignment />}
                      onClick={() =>
                        navigate(`/pp/inspections/${submission.permitNumber}`)
                      }
                    >
                      View Inspections
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<PhotoCamera />}
                      color="success"
                      onClick={() => setPhotoCaptureOpen(true)}
                    >
                      Capture Photos
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      color="secondary"
                      onClick={() => navigate("/pp/photos")}
                    >
                      Photo Gallery
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Add Note Dialog */}
      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note about this submission..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
          >
            Add Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Dialog */}
      <Dialog
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        maxWidth="lg"
        fullWidth
        fullScreen={window.innerWidth < 768}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">
              {selectedFile?.name || "File Viewer"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selectedFiles.length > 1 && (
                <Typography variant="body2" color="text.secondary">
                  {currentFileIndex + 1} of {selectedFiles.length}
                </Typography>
              )}
              <IconButton onClick={handleCloseFileViewer} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: 500 }}>
          {selectedFile && (
            <Box
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              {/* File Navigation */}
              {selectedFiles.length > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Button
                    startIcon={<ArrowBackIos />}
                    onClick={handlePrevFile}
                    disabled={currentFileIndex === 0}
                    variant="outlined"
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.name}
                  </Typography>
                  <Button
                    endIcon={<ArrowForwardIos />}
                    onClick={handleNextFile}
                    disabled={currentFileIndex === selectedFiles.length - 1}
                    variant="outlined"
                  >
                    Next
                  </Button>
                </Box>
              )}

              {/* File Content */}
              <Box sx={{ flexGrow: 1, p: 2 }}>
                {selectedFile.name.toLowerCase().endsWith(".pdf") ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "60vh",
                      border: 1,
                      borderColor: "grey.300",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        📄 {selectedFile.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        PDF viewer would be displayed here
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => handleDownloadSingleFile(selectedFile)}
                      >
                        Download PDF
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "60vh",
                      border: 1,
                      borderColor: "grey.300",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.50",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        📁 {selectedFile.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        File preview would be displayed here
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => handleDownloadSingleFile(selectedFile)}
                      >
                        Download File
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileViewer}>Close</Button>
          {selectedFile && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => handleDownloadSingleFile(selectedFile)}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Photo Capture Dialog */}
      <PpMobilePhotoCaptureComponent
        open={photoCaptureOpen}
        onClose={() => setPhotoCaptureOpen(false)}
        onPhotosUploaded={(photos) => {
          setPhotoCaptureOpen(false);
        }}
        category="submission"
        propertyId={submission?.id}
      />
    </Box>
  );
};

export default PpSubmissionDetailsPage;
